import React, { useEffect, useState } from 'react';
import { Box, Heading, Text, SimpleGrid, HStack, VStack, Icon } from '@chakra-ui/react';
import { FiAlertTriangle, FiCheckCircle } from 'react-icons/fi';
import Page from '../../components/ui/Page';
import SectionCard from '../../components/ui/SectionCard';
import Breadcrumbs from '../../components/ui/Breadcrumbs';
import LoadingState from '../../components/ui/LoadingState';
import ErrorState from '../../components/ui/ErrorState';
import client from '../../utils/fetchClient';
import { useProjectContext } from '../../context/ProjectContext';

const tile = (label, value, sub, color = 'gray.900') => (
  <Box bg="white" border="1px solid" borderColor="gray.200" borderRadius="14px" p={4}>
    <Text fontSize="xs" color="gray.500" mb={1.5}>{label}</Text>
    <Text fontSize="26px" fontWeight="800" color={color}>{value}</Text>
    {sub && <Text fontSize="xs" color="gray.400">{sub}</Text>}
  </Box>
);

export default function ReleaseReadinessPage() {
  const { effectiveProjectParam } = useProjectContext();
  const [state, setState] = useState({ loading: true, error: null, metrics: null });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const q = effectiveProjectParam ? { project: effectiveProjectParam } : {};
        const m = await client.get('/api/metrics', { query: q });
        const metrics = m.ok ? (await m.json()).summary : null;
        if (!cancelled) setState({ loading: false, error: null, metrics });
      } catch (e) {
        if (!cancelled) setState({ loading: false, error: e.message || 'Failed to load', metrics: null });
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveProjectParam]);

  const { loading, error, metrics } = state;
  const passRate = metrics?.passRate ?? null;
  const failing = metrics?.failedTests ?? 0;

  const ready = passRate != null && passRate >= 98 && failing === 0;
  const atRisk = (passRate != null && passRate < 90) || failing > 20;
  const verdict = ready
    ? { label: 'Ready to release', color: 'status.pass', bg: 'status.passBg', icon: FiCheckCircle }
    : atRisk
      ? { label: 'At risk', color: 'status.flakyText', bg: 'status.flakyBg', icon: FiAlertTriangle }
      : { label: 'Proceed with caution', color: 'status.flakyText', bg: 'status.flakyBg', icon: FiAlertTriangle };

  return (
    <Page maxW="6xl">
      <Breadcrumbs items={[{ label: 'AI Analysis', to: '/analysis/release-readiness' }, { label: 'Release readiness' }]} />
      <Heading size="lg" fontWeight="800" letterSpacing="-0.5px" color="gray.900" mb={1}>Release readiness</Heading>
      <Text fontSize="sm" color="gray.500" mb={5}>Automated signal on whether the current build is safe to ship.</Text>

      {loading && <LoadingState message="Assessing readiness" />}
      {error && <ErrorState message={error} onRetry={() => window.location.reload()} />}

      {!loading && !error && (
        <VStack align="stretch" spacing={5}>
          <SectionCard>
            <HStack spacing={3} mb={3}>
              <Icon as={verdict.icon} color={verdict.color} boxSize={5} />
              <Box as="span" bg={verdict.bg} color={verdict.color} fontWeight="700" fontSize="sm" px={3.5} py={1.5} borderRadius="full">
                {verdict.label}
              </Box>
            </HStack>
            <Text fontSize="sm" color="gray.700" maxW="620px" lineHeight="1.6">
              {failing > 0 && `${failing} failing test${failing > 1 ? 's' : ''} in the latest runs. `}
              {failing === 0 && 'No failing tests against this build. '}
              Pass rate is {passRate != null ? `${passRate}%` : 'unknown'}.
            </Text>
          </SectionCard>

          <SimpleGrid columns={{ base: 2, md: 2 }} spacing={3.5}>
            {tile('Pass rate', passRate != null ? `${passRate}%` : '-')}
            {tile('Failing tests', failing, null, failing ? 'status.fail' : 'gray.900')}
          </SimpleGrid>
        </VStack>
      )}
    </Page>
  );
}
